export type SupportedCurrency = "USD" | "KRW" | "JPY";

export interface CurrencyConfig {
  code: SupportedCurrency;
  label: string;
  locale: string;
  lessThanThreshold: number;
}

export const CURRENCY_CONFIGS: Record<SupportedCurrency, CurrencyConfig> = {
  USD: {
    code: "USD",
    label: "US Dollar",
    locale: "en-US",
    lessThanThreshold: 0.01,
  },
  KRW: {
    code: "KRW",
    label: "Korean Won",
    locale: "ko-KR",
    lessThanThreshold: 1,
  },
  JPY: {
    code: "JPY",
    label: "Japanese Yen",
    locale: "ja-JP",
    lessThanThreshold: 1,
  },
};

/** Get the currency symbol (e.g. "$", "₩", "¥") via the Intl API */
export function getCurrencySymbol(currency: SupportedCurrency): string {
  const config = getCurrencyConfig(currency);
  return (
    new Intl.NumberFormat(config.locale, { style: "currency", currency: config.code })
      .formatToParts(0)
      .find((p) => p.type === "currency")?.value ?? config.code
  );
}

export function getCurrencyConfig(currency: string | undefined): CurrencyConfig {
  if (currency && currency in CURRENCY_CONFIGS) {
    return CURRENCY_CONFIGS[currency as SupportedCurrency];
  }
  return CURRENCY_CONFIGS.USD;
}

/**
 * Gets the price in the selected currency from token data.
 *
 * @param prices - Token prices object (e.g., { KRW: "526000000", JPY: "62000000" })
 * @param priceUSD - USD price as string or number
 * @param currency - Target currency code
 * @returns Price in the target currency
 */
export function getCurrencyPriceWithFallback(
  prices: Record<string, string> | undefined,
  priceUSD: string | number | undefined,
  currency: SupportedCurrency,
): number {
  if (currency === "USD" && priceUSD) {
    return Number(priceUSD);
  }

  if (prices?.[currency]) {
    return Number(prices[currency]);
  }

  return 0;
}
